/** Normal PDF: f(x) = (1/(σ√(2π))) * exp(-(x-μ)²/(2σ²)) */
export function normalPdf(x: number, mu: number, sigma: number): number {
  const coeff = 1 / (sigma * Math.sqrt(2 * Math.PI));
  const exponent = -((x - mu) ** 2) / (2 * sigma ** 2);
  return coeff * Math.exp(exponent);
}

/** Generate [x, y] data points for a normal PDF */
export function generatePdfData(
  mu: number,
  sigma: number,
  xMin: number,
  xMax: number,
  numPoints = 300,
): Array<[number, number]> {
  const step = (xMax - xMin) / numPoints;
  const data: Array<[number, number]> = [];
  for (let i = 0; i <= numPoints; i++) {
    const x = xMin + i * step;
    data.push([parseFloat(x.toFixed(4)), parseFloat(normalPdf(x, mu, sigma).toFixed(6))]);
  }
  return data;
}

/**
 * Standard normal CDF Φ(z) using Abramowitz & Stegun approximation (26.2.17).
 * |error| < 7.5e-8
 */
export function normalCdf(z: number): number {
  if (z < -8) {
    return 0;
  }
  if (z > 8) {
    return 1;
  }

  const a1 = 0.254829592;
  const a2 = -0.284496736;
  const a3 = 1.421413741;
  const a4 = -1.453152027;
  const a5 = 1.061405429;
  const p = 0.3275911;

  const sign = z < 0 ? -1 : 1;
  const absZ = Math.abs(z) / Math.SQRT2;
  const t = 1 / (1 + p * absZ);
  const y = 1 - ((((a5 * t + a4) * t + a3) * t + a2) * t + a1) * t * Math.exp(-absZ * absZ);

  return 0.5 * (1 + sign * y);
}

/** General normal CDF: P(X ≤ x) where X ~ N(μ, σ²) */
export function normalCdfGeneral(x: number, mu: number, sigma: number): number {
  return normalCdf((x - mu) / sigma);
}

/**
 * Inverse standard normal (quantile / probit) using Acklam's rational approximation.
 * Accurate to ~1.15e-9.
 */
export function normalInv(p: number): number {
  if (p <= 0) {
    return -Infinity;
  }
  if (p >= 1) {
    return Infinity;
  }
  if (p === 0.5) {
    return 0;
  }

  const a = [
    -3.969683028665376e1, 2.209460984245205e2, -2.759285104469687e2, 1.38357751867269e2, -3.066479806614716e1,
    2.506628277459239,
  ];
  const b = [
    -5.447609879822406e1, 1.615858368580409e2, -1.556989798598866e2, 6.680131188771972e1, -1.328068155288572e1,
  ];
  const c = [
    -7.784894002430293e-3, -3.223964580411365e-1, -2.400758277161838, -2.549732539343734, 4.374664141464968,
    2.938163982698783,
  ];
  const d = [7.784695709041462e-3, 3.224671290700398e-1, 2.445134137142996, 3.754408661907416];

  const pLow = 0.02425;
  const pHigh = 1 - pLow;
  let q: number, r: number;

  if (p < pLow) {
    q = Math.sqrt(-2 * Math.log(p));
    return (
      (((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  } else if (p <= pHigh) {
    q = p - 0.5;
    r = q * q;
    return (
      ((((((a[0] * r + a[1]) * r + a[2]) * r + a[3]) * r + a[4]) * r + a[5]) * q) /
      (((((b[0] * r + b[1]) * r + b[2]) * r + b[3]) * r + b[4]) * r + 1)
    );
  } else {
    q = Math.sqrt(-2 * Math.log(1 - p));
    return (
      -(((((c[0] * q + c[1]) * q + c[2]) * q + c[3]) * q + c[4]) * q + c[5]) /
      ((((d[0] * q + d[1]) * q + d[2]) * q + d[3]) * q + 1)
    );
  }
}

/** Inverse general normal: find x such that P(X ≤ x) = p where X ~ N(μ, σ²) */
export function normalInvGeneral(p: number, mu: number, sigma: number): number {
  return mu + sigma * normalInv(p);
}
